import { useState } from 'react';
import { X, Star } from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import { getApiBaseUrl } from '../utils/config';
import { toast } from 'sonner';

interface SupportRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
}

export default function SupportRatingModal({ isOpen, onClose, ticketId }: SupportRatingModalProps) {
  const isDarkMode = useTheme((state) => state.theme);
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(`${apiBaseUrl}/api/v1/support/tickets/${ticketId}/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rating,
          feedback: feedback.trim() || undefined,
        }),
      });

      if (response.ok) {
        toast.success('Thank you for your feedback!');
        onClose();
        // Reset form
        setRating(0);
        setFeedback('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        className={`relative w-full max-w-md rounded-xl shadow-2xl ${
          isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
              Rate Your Support Experience
            </h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-[#1a2424]' : 'hover:bg-gray-100'
              }`}
            >
              <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            How would you rate the support you received?
          </p>

          {/* Star Rating */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                disabled={isSubmitting}
                className="transition-transform hover:scale-110 disabled:opacity-50"
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    star <= (hoveredRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : isDarkMode
                      ? 'text-gray-600'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Rating Label */}
          {rating > 0 && (
            <p className={`text-center text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          )}

          {/* Feedback Text Area */}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Additional Feedback (Optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={isSubmitting}
              placeholder="Tell us more about your experience..."
              rows={4}
              className={`w-full px-4 py-2 rounded-lg border resize-none ${
                isDarkMode
                  ? 'bg-[#0f1919] border-[#1a2424] text-white placeholder-gray-500'
                  : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] disabled:opacity-50`}
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0}
            className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
              isSubmitting || rating === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#1a9f6b] hover:bg-[#168a5c]'
            } text-white`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Rating'}
          </button>
        </div>
      </div>
    </div>
  );
}
